import React, {
  useEffect,
  useState,
  useRef,
  RefObject,
  useCallback,
} from 'react';
import { History } from 'history';

import { iframePostMessage } from 'utils/constants';
import { loadAuthToken, loadSessionId } from 'utils/tokenStorage';

import './styles.scss';
import { Config } from 'utils/config/config';

interface IProps {
  history: History;
}

const DEFAULT_IFRAME_DOCUMENT_HEIGHT = 322;

const Main = (props: IProps) => {
  const somrystIframeRef = useRef<HTMLIFrameElement | null>(null);
  const resetIframeRef = useRef<HTMLIFrameElement | null>(null);
  const resetOIframeRef = useRef<HTMLIFrameElement | null>(null);

  const [resetIframe, setResetIframe] = useState<Window | null | undefined>(
    undefined,
  );
  const [resetOIframe, setResetOIframe] = useState<Window | null | undefined>(
    undefined,
  );
  const [somrystIframe, setSomrystIframe] = useState<Window | null | undefined>(
    undefined,
  );

  const onIframeLoaded = useCallback(
    (params: {
      state: string;
      iframe: Window | null | undefined;
      ref: RefObject<HTMLIFrameElement | null>;
      host: string;
    }) => {
      let iframeWindow: Window | null | undefined;
      const { state, iframe, ref, host } = params;
      if (!iframe) {
        iframeWindow = ref.current?.contentWindow;
        const data = {
          authToken: loadAuthToken(),
          sessionId: loadSessionId(),
        };

        // Set timeout to ensure iframe is ready to receive message event on Safari
        /* istanbul ignore next */
        setTimeout(() => {
          iframeWindow?.postMessage(data, host);

          if (state === 'resetIframe') {
            setResetIframe(iframeWindow);
          }

          if (state === 'resetOIframe') {
            setResetOIframe(iframeWindow);
          }

          if (state === 'somrystIframe') {
            setSomrystIframe(iframeWindow);
          }
        }, 500);
      }
    },
    [],
  );

  const renderSomrystDocumentIframe = () => {
    if (Config.SOMRYST_WEB_URL) {
      return (
        <iframe
          data-testid={
            somrystIframe ? 'somryst-document-iframe-loaded' : 'somryst-iframe'
          }
          ref={somrystIframeRef}
          src={`${Config.SOMRYST_WEB_URL}/document`}
          title="somryst-document-iframe"
          styleName="somryst-iframe"
          onLoad={() =>
            onIframeLoaded({
              state: 'somrystIframe',
              iframe: somrystIframe,
              ref: somrystIframeRef,
              host: Config.SOMRYST_WEB_URL,
            })
          }
        />
      );
    }

    return (
      <div
        data-testid={
          somrystIframe ? 'somryst-document-iframe-loaded' : 'somryst-iframe'
        }
      >
        Please define SOMRYST_WEB_URL
      </div>
    );
  };

  const renderResetDocumentIframe = () => {
    const iframeURL = `${Config.HYLIA_WEB_URL}/reset/document`;

    if (Config.HYLIA_WEB_URL) {
      return (
        <iframe
          scrolling="no"
          data-testid={
            resetIframe ? 'reset-document-iframe-loaded' : 'reset-iframe'
          }
          ref={resetIframeRef}
          src={iframeURL}
          title="reset-document-iframe"
          styleName="reset-iframe"
          onLoad={() =>
            onIframeLoaded({
              state: 'resetIframe',
              iframe: resetIframe,
              ref: resetIframeRef,
              host: Config.HYLIA_WEB_URL,
            })
          }
          height={DEFAULT_IFRAME_DOCUMENT_HEIGHT}
        />
      );
    }

    return (
      <div
        data-testid={
          resetIframe ? 'reset-document-iframe-loaded' : 'reset-iframe'
        }
      >
        Please define HYLIA_WEB_URL
      </div>
    );
  };

  const renderResetODocumentIframe = () => {
    const iframeURL = `${Config.HYLIA_WEB_URL}/reset-o/document`;

    if (Config.HYLIA_WEB_URL) {
      return (
        <iframe
          scrolling="no"
          data-testid={
            resetOIframe ? 'resetO-document-iframe-loaded' : 'resetO-iframe'
          }
          ref={resetOIframeRef}
          src={iframeURL}
          title="resetO-document-iframe"
          styleName="resetO-iframe"
          onLoad={() =>
            onIframeLoaded({
              state: 'resetOIframe',
              iframe: resetOIframe,
              ref: resetOIframeRef,
              host: Config.HYLIA_WEB_URL,
            })
          }
          height={DEFAULT_IFRAME_DOCUMENT_HEIGHT}
        />
      );
    }

    return (
      <div
        data-testid={
          resetOIframe ? 'resetO-document-iframe-loaded' : 'resetO-iframe'
        }
      >
        Please define HYLIA_WEB_URL
      </div>
    );
  };

  const handleIframeMessage = useCallback(
    (event: any) => {
      const { payload } = event.data;
      if (payload && payload.action === iframePostMessage.LOADED) {
        const { program, url } = payload;
        props.history.push(url, {
          program,
        });
      }
    },
    [props.history],
  );

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [handleIframeMessage]);

  return (
    <div data-testid="documents-dashboard">
      <div styleName="header-wrapper">
        <div styleName="header-title">Documents</div>
      </div>

      <div styleName="wrapper">
        <div styleName="somryst-document" data-testid="somryst-document-iframe">
          {renderSomrystDocumentIframe()}
        </div>

        <div styleName="reset-document" data-testid="reset-document-iframe">
          {renderResetDocumentIframe()}
        </div>

        <div styleName="resetO-document" data-testid="resetO-document-iframe">
          {renderResetODocumentIframe()}
        </div>
      </div>

      <div styleName="disclaimer-container">
        <p styleName="disclaimer-text">
          These documents may change without notice. This website will always
          contain the most current version.
        </p>
        <p styleName="disclaimer-text">
          If participating in an investigational study, the documents on this
          website may not apply. Please contact the study coordinator.
        </p>
      </div>
    </div>
  );
};

export default Main;
